import { useState } from 'react';

import { Trans } from '@lingui/react/macro';
import InfoIcon from '@mui/icons-material/Info';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { usePatientVisits } from '@/react-query/usePatientVisits';

import { RerunAi } from '../../../../modules/annotator/findingSidebar/RerunAi';
import { enabledHDAtom } from '../../../../modules/annotator/multiFileViewer/imageToolbar/atoms';
import type { ImageObject } from '../../../../modules/annotator/types';
import { useClearSortForVisits } from '../../../../react-query/useClearSortForVisits';
import { usePatientRblData } from '../../../../react-query/usePatientRblData';
import { usePreferences } from '../../../../react-query/usePreferences';
import { useDevtoolsShortcut } from '../../../../utils/hooks/useDevtoolsShortcut';
import {
  formatInformationToDisplay,
  getEstimatedVisitDate,
} from '../helpers/helpers';

import { InformationListItem } from './InformationListItem';

type VideaDevToolProps = {
  activeImage: any;
  sortedAndFilteredImages: any[];
};

export const VideaDevTool = ({
  activeImage,
  sortedAndFilteredImages,
}: VideaDevToolProps) => {
  // params
  const { id: patientId } = useParams<{ id: string }>();

  // atoms
  const enabledHD = useAtomValue(enabledHDAtom);

  // state
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // selectors
  const user = useSelector((state: any) => state?.login?.user);
  const patients = useSelector((state: any) => state.annotator?.patients) || {};

  // variables
  const currentPatient = patients[patientId] || {};
  const { images = {} } = currentPatient;
  const videaPatientId = currentPatient?.patientId;
  const currentPatientImage = Object.values(images).at(0) as ImageObject;

  const imagesArray: ImageObject[] = [
    ...(currentPatient?.images ? Object.values(currentPatient.images) : []),
    ...sortedAndFilteredImages,
  ];

  const imageManifestsToNotShowInRbl = [
    ...new Set(
      imagesArray
        .map((image) => Number(image?.imageManifestId ?? image?.imageId))
        .filter((id) => !isNaN(id)),
    ),
  ];

  // hooks
  const { data } = usePreferences();
  const { data: patientVisitsData } = usePatientVisits({
    patientId,
  });

  const estimatedVisitId = activeImage
    ? activeImage.estimatedVisitId
    : currentPatientImage?.estimatedVisitId;

  const estimatedVisitDate = getEstimatedVisitDate(
    estimatedVisitId,
    patientVisitsData?.visits || [],
  );
  const { isLoading: isClearingSorts, mutateAsync: clearSorts } =
    useClearSortForVisits(estimatedVisitId ? [Number(estimatedVisitId)] : []);

  const { data: patientRblData } = usePatientRblData({
    imageManifestIdsToSkip: imageManifestsToNotShowInRbl,
    estimatedVisitId,
    videaPatientId,
    shouldMakeApiRequest: isDialogOpen && !activeImage && !!videaPatientId,
  });

  const isPreferDisplayOverSharpened = data?.preferDisplayOverSharpened;

  const informationToDisplay = formatInformationToDisplay(
    activeImage,
    user,
    currentPatient,
    estimatedVisitDate ?? null,
    enabledHD,
    !!isPreferDisplayOverSharpened,
    patientRblData?.imageManifestsForRbl,
  );

  const { showDevtools } = useDevtoolsShortcut();

  const handleClickOpen = () => {
    setIsDialogOpen(true);
  };

  const handleClose = () => {
    setIsDialogOpen(false);
  };

  if (!showDevtools) {
    return <></>;
  }

  return (
    <>
      <IconButton
        aria-label="delete"
        size="small"
        onClick={handleClickOpen}
        data-testid="videa-dev-tool"
      >
        <InfoIcon fontSize="inherit" color="primary" />
      </IconButton>
      <Dialog
        open={isDialogOpen}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Information</DialogTitle>
        <DialogContent>
          {informationToDisplay.map(
            (infoToDisplay) =>
              infoToDisplay.data.length > 0 && (
                <InformationListItem
                  key={infoToDisplay.title}
                  informationList={infoToDisplay.data}
                  title={infoToDisplay.title}
                />
              ),
          )}
          <br />
          {activeImage ? (
            <>
              <p>
                <Trans>Rerun analysis for selected image</Trans>
              </p>
              <RerunAi
                estimatedVisitId={estimatedVisitId}
                images={[currentPatientImage]}
                selectedPatientId={currentPatient}
                isInRBLMenu={true}
              />
            </>
          ) : (
            <>
              <p>
                <Trans>Rerun analysis for all images displayed</Trans>
              </p>
              <RerunAi
                estimatedVisitId={estimatedVisitId}
                images={sortedAndFilteredImages}
                selectedPatientId={currentPatient}
                isInRBLMenu={true}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => clearSorts()}
            color="primary"
            disabled={!estimatedVisitId || isClearingSorts}
          >
            Clear Visit Sorts
          </Button>
          <Button onClick={handleClose} color="primary" autoFocus>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
